import { produce } from 'immer'
import { clone, indexBy, pathEq, prop } from 'ramda'

import { RULE_TYPE_OPTIONS } from 'components/Routing/Rule/constants'
import { tempId } from 'utils'

import * as constants from './constants'
import { cleanRules } from './helpers'

const DEFAULT_STATE = {
  published: {
    rules: [],
    ruleGroups: [],
    ruleTree: null,
    defaultRuleUsers: [],
  },
  draft: {
    rules: [],
    ruleGroups: [],
    rulesById: {},
    ruleGroupsById: {},
    deletedRules: [],
    deletedRuleGroups: [],
    defaultRuleUsers: [],
  },
  ui: {
    addingNewRule: {
      showing: false,
      forRuleId: null,
    },
    editingAction: {
      showing: false,
      forRuleId: null,
    },
    saving: false,
    savingError: null,
    showingPublished: false,
    selectingContinueToGroup: {
      showing: false,
      forRuleId: null,
      forDefaultActionGroupId: null,
    },
    hideFallbackOptInFlow: false,
  },
  teams: [],
}

const receiveAllData = (newState, payload) => {
  newState.teams = payload.teams

  newState.published.ruleTree = payload.ruleTree

  const cleanedRules = cleanRules(payload.rules)

  newState.published.rules = cleanedRules
  newState.draft.rules = cleanedRules
  newState.draft.rulesById = indexBy(prop('id'), cleanedRules)

  newState.published.ruleGroups = payload.ruleGroups
  newState.draft.ruleGroups = payload.ruleGroups
  newState.draft.ruleGroupsById = indexBy(prop('id'), payload.ruleGroups)

  newState.published.defaultRuleUsers = payload.defaultRuleUsers
  newState.draft.defaultRuleUsers = payload.defaultRuleUsers

  return newState
}

const addNewRuleToDraft = (newState, payload) => {
  const { groupId, indexInGroup, routingRuleIdField } = payload

  const newRule = {
    id: tempId(),
    name: '',
    action: undefined,
    conditions: undefined,
    status: 'ENABLED',
    ruleType: RULE_TYPE_OPTIONS.DEFAULT,
  }

  // add the rule to state
  newState.draft.rules.push(newRule)
  newState.draft.rulesById[newRule.id] = newRule

  // add the rule to the rule group
  const groupById = newState.draft.ruleGroupsById[groupId]
  const group = newState.draft.ruleGroups.find((group) => group.id === groupId)
  if (indexInGroup === 'LAST') {
    group[routingRuleIdField].push(newRule.id)
    groupById[routingRuleIdField].push(newRule.id)
  } else {
    group[routingRuleIdField].splice(indexInGroup, 0, newRule.id)
    groupById[routingRuleIdField].splice(indexInGroup, 0, newRule.id)
  }

  // set show adding new rule
  newState.ui.addingNewRule.showing = true
  newState.ui.addingNewRule.forRuleId = newRule.id

  return newState
}

const updateRuleInDraft = (newState, payload) => {
  const { ruleUpdates, ruleId } = payload

  const updatedRule = {
    ...newState.draft.rulesById[ruleId],
    ...ruleUpdates,
  }

  newState.draft.rulesById[ruleId] = updatedRule
  newState.draft.rules = newState.draft.rules.map((rule) => {
    if (rule.id === ruleId) {
      return updatedRule
    } else {
      return rule
    }
  })

  return newState
}

const deleteRuleInDraft = (newState, payload) => {
  const { ruleId, routingRuleIdField } = payload

  const deletedRule = newState.draft.rulesById[ruleId]

  newState.draft.deletedRules.push(deletedRule)

  newState.draft.ruleGroups = newState.draft.ruleGroups.map((ruleGroup) => ({
    ...ruleGroup,
    [routingRuleIdField]: ruleGroup[routingRuleIdField].filter(
      (id) => id !== ruleId
    ),
  }))
  newState.draft.ruleGroupsById = indexBy(prop('id'), newState.draft.ruleGroups)

  // make sure we dont have groups without rules
  const emptyGroups = newState.draft.ruleGroups.filter(
    (ruleGroup) =>
      ruleGroup.place !== 1 && ruleGroup[routingRuleIdField].length === 0
  )
  emptyGroups.forEach((group) =>
    deleteRuleGroup(newState, { groupId: group.id })
  )

  return newState
}

const setShowAddingNewRule = (newState, payload) => {
  const { showing, ruleId } = payload

  newState.ui.addingNewRule.showing = showing
  newState.ui.addingNewRule.forRuleId = ruleId

  return newState
}

const setShowEditAction = (newState, payload) => {
  const { showing, ruleId } = payload

  newState.ui.editingAction.showing = showing
  newState.ui.editingAction.forRuleId = ruleId

  return newState
}

const setHideFallbackOptInFlow = (newState, payload) => {
  const { shouldHide } = payload
  newState.ui.hideFallbackOptInFlow = shouldHide
  return newState
}

const pointRuleToNewGroup = (newState, payload) => {
  const { ruleId, routingRuleIdField } = payload

  const rule = newState.draft.rulesById[ruleId]

  const newRuleForNewGroup = {
    id: tempId(),
    name: '',
    action: undefined,
    conditions: undefined,
    status: 'ENABLED',
  }

  const newRuleGroup = {
    treeId: newState.published.ruleTree.id,
    id: tempId(),
    [routingRuleIdField]: [newRuleForNewGroup.id],
    defaultAction: undefined,
    place: newState.draft.ruleGroups.length + 1,
  }

  const updatedRule = {
    ...rule,
    action: {
      type: 'EXECUTE_RULE_GROUP',
      options: {
        nextRuleGroupId: newRuleGroup.id,
      },
    },
    ruleType: RULE_TYPE_OPTIONS.DEFAULT,
  }

  newState.draft.rules.push(newRuleForNewGroup)
  newState.draft.rulesById[newRuleForNewGroup.id] = newRuleForNewGroup
  newState.draft.ruleGroups.push(newRuleGroup)
  newState.draft.ruleGroupsById[newRuleGroup.id] = newRuleGroup
  newState.draft.rulesById[ruleId] = updatedRule
  newState.draft.rules = newState.draft.rules.map((rule) => {
    if (rule.id === ruleId) {
      return updatedRule
    } else {
      return rule
    }
  })
  return newState
}

const updateDraftDefaultRuleGroupAction = (newState, payload) => {
  const { groupId, action } = payload

  const group = newState.draft.ruleGroupsById[groupId]
  const updatedGroup = {
    ...group,
    defaultAction: action,
  }

  newState.draft.ruleGroupsById[groupId] = updatedGroup
  newState.draft.ruleGroups = newState.draft.ruleGroups.map((group) => {
    if (group.id === groupId) {
      return updatedGroup
    } else {
      return group
    }
  })
  return newState
}

const pointDefaultGroupActionToNewGroup = (newState, payload) => {
  const { groupId, routingRuleIdField } = payload

  const group = newState.draft.ruleGroupsById[groupId]

  const newRuleForNewGroup = {
    id: tempId(),
    name: '',
    action: undefined,
    conditions: undefined,
    status: 'ENABLED',
  }

  const newRuleGroup = {
    treeId: newState.published.ruleTree.id,
    id: tempId(),
    [routingRuleIdField]: [newRuleForNewGroup.id],
    defaultAction: undefined,
    place: newState.draft.ruleGroups.length + 1,
  }

  const updatedRuleGroup = {
    ...group,
    defaultAction: {
      type: 'EXECUTE_RULE_GROUP',
      options: {
        nextRuleGroupId: newRuleGroup.id,
      },
    },
  }

  newState.draft.rules.push(newRuleForNewGroup)
  newState.draft.rulesById[newRuleForNewGroup.id] = newRuleForNewGroup
  newState.draft.ruleGroups.push(newRuleGroup)
  newState.draft.ruleGroupsById[newRuleGroup.id] = newRuleGroup
  newState.draft.ruleGroupsById[groupId] = updatedRuleGroup
  newState.draft.ruleGroups = newState.draft.ruleGroups.map((group) => {
    if (group.id === groupId) {
      return updatedRuleGroup
    } else {
      return group
    }
  })
  return newState
}

const startDraftSaving = (newState) => {
  newState.ui.saving = true
  newState.ui.savingError = null
  return newState
}

const recordDraftSaved = (newState, payload) => {
  newState.ui.saving = false
  const cleanedRules = cleanRules(payload.rules)
  newState.published.rules = cleanedRules
  newState.draft.rules = cleanedRules
  newState.draft.rulesById = indexBy(prop('id'), cleanedRules)

  newState.published.ruleGroups = payload.ruleGroups
  newState.draft.ruleGroups = payload.ruleGroups
  newState.draft.ruleGroupsById = indexBy(prop('id'), payload.ruleGroups)

  newState.draft.defaultRuleUsers = payload.defaultRuleUsers
  newState.published.defaultRuleUsers = payload.defaultRuleUsers

  newState.draft.deletedRules = []
  newState.draft.deletedRuleGroups = []

  return newState
}

const failedSavingDraft = (newState, payload) => {
  newState.ui.saving = false
  newState.ui.savingError = payload
  return newState
}

const revertToPublishedVersion = (newState) => {
  const publishedRules = newState.published.rules
  const publishedRuleGroups = newState.published.ruleGroups
  const onCallUsers = newState.published.defaultRuleUsers

  newState.draft.rules = publishedRules
  newState.draft.rulesById = indexBy(prop('id'), publishedRules)

  newState.draft.ruleGroups = publishedRuleGroups
  newState.draft.ruleGroupsById = indexBy(prop('id'), publishedRuleGroups)

  newState.draft.deletedRules = []
  newState.draft.deletedRuleGroups = []
  newState.draft.defaultRuleUsers = onCallUsers

  return newState
}

const setShowPublished = (newState, payload) => {
  newState.ui.showingPublished = payload.showingPublished
  return newState
}

const updateDraftDefaultUserIds = (newState, payload) => {
  newState.draft.defaultRuleUsers = payload.userIds
  return newState
}

const showSelectContinueToGroup = (newState, payload) => {
  newState.ui.selectingContinueToGroup.showing = true
  newState.ui.selectingContinueToGroup.forRuleId = payload.ruleId
  newState.ui.selectingContinueToGroup.forDefaultActionGroupId = payload.groupId

  return newState
}

const chooseSelectContinueToGroup = (newState, payload) => {
  const selectedGroupId = payload.groupId

  const groupId = newState.ui.selectingContinueToGroup.forDefaultActionGroupId
  const ruleId = newState.ui.selectingContinueToGroup.forRuleId

  if (groupId) {
    const group = newState.draft.ruleGroupsById[groupId]

    const updatedRuleGroup = {
      ...group,
      defaultAction: {
        type: 'EXECUTE_RULE_GROUP',
        options: {
          nextRuleGroupId: selectedGroupId,
        },
      },
    }

    newState.draft.ruleGroupsById[groupId] = updatedRuleGroup
    newState.draft.ruleGroups = newState.draft.ruleGroups.map((group) => {
      if (group.id === groupId) {
        return updatedRuleGroup
      } else {
        return group
      }
    })
  } else if (ruleId) {
    const rule = newState.draft.rulesById[ruleId]

    const updatedRule = {
      ...rule,
      action: {
        type: 'EXECUTE_RULE_GROUP',
        options: {
          nextRuleGroupId: selectedGroupId,
        },
      },
      ruleType: RULE_TYPE_OPTIONS.DEFAULT,
    }

    newState.draft.rulesById[ruleId] = updatedRule
    newState.draft.rules = newState.draft.rules.map((rule) => {
      if (rule.id === ruleId) {
        return updatedRule
      } else {
        return rule
      }
    })
  }

  newState.ui.selectingContinueToGroup.showing = false
  newState.ui.selectingContinueToGroup.forRuleId = null
  newState.ui.selectingContinueToGroup.forDefaultActionGroupId = null

  return newState
}

const cancelSelectContinueToGroup = (newState) => {
  newState.ui.selectingContinueToGroup.showing = false
  newState.ui.selectingContinueToGroup.forRuleId = null
  newState.ui.selectingContinueToGroup.forDefaultActionGroupId = null

  return newState
}

const deleteRuleGroup = (newState, { groupId }) => {
  const rulePointsToDeletedGroup = pathEq(
    ['action', 'options', 'nextRuleGroupId'],
    groupId
  )
  const ruleGroupPointsToDeletedGroup = pathEq(
    ['defaultAction', 'options', 'nextRuleGroupId'],
    groupId
  )

  newState.draft.rules = newState.draft.rules.map((rule) => {
    if (rulePointsToDeletedGroup(rule)) {
      rule.action = undefined
    }
    return rule
  })
  newState.draft.rulesById = indexBy(prop('id'), newState.draft.rules)

  newState.draft.ruleGroups = newState.draft.ruleGroups.map((ruleGroup) => {
    if (ruleGroupPointsToDeletedGroup(ruleGroup)) {
      ruleGroup.defaultAction = undefined
    }
    return ruleGroup
  })
  newState.draft.ruleGroupsById = indexBy(prop('id'), newState.draft.ruleGroups)

  return newState
}

const moveRule = (newState, payload) => {
  const {
    from: { groupId: oldGroupId, id: ruleId },
    to: { groupId: newGroupId, place },
    routingRuleIdField,
  } = payload

  const rule = newState.draft.rulesById[ruleId]

  // add the rule to the rule group
  const oldGroupById = newState.draft.ruleGroupsById[oldGroupId]
  const oldGroup = newState.draft.ruleGroups.find(
    (group) => group.id === oldGroupId
  )
  const newGroupById = newState.draft.ruleGroupsById[newGroupId]
  const newGroup = newState.draft.ruleGroups.find(
    (group) => group.id === newGroupId
  )

  oldGroup[routingRuleIdField] = oldGroup[routingRuleIdField].filter(
    (id) => id !== ruleId
  )
  oldGroupById[routingRuleIdField] = oldGroupById[routingRuleIdField].filter(
    (id) => id !== ruleId
  )

  if (place === 'LAST') {
    newGroup[routingRuleIdField].push(rule.id)
    newGroupById[routingRuleIdField].push(rule.id)
  } else {
    newGroup[routingRuleIdField].splice(place, 0, rule.id)
    newGroupById[routingRuleIdField].splice(place, 0, rule.id)
  }

  return newState
}

const reducer = (state = DEFAULT_STATE, action) => {
  const newState = clone(state)

  switch (action.type) {
    case constants.RECEIVE_ALL_RULE_DATA:
      return produce(state, (draftState) =>
        receiveAllData(draftState, action.payload)
      )
    case constants.NEW_RULE:
      return produce(state, (draftState) =>
        addNewRuleToDraft(draftState, action.payload)
      )
    case constants.UPDATE_RULE:
      return produce(state, (draftState) =>
        updateRuleInDraft(draftState, action.payload)
      )
    case constants.DELETE_RULE:
      return produce(state, (draftState) =>
        deleteRuleInDraft(draftState, action.payload)
      )
    case constants.POINT_RULE_TO_NEW_GROUP:
      return produce(state, (draftState) =>
        pointRuleToNewGroup(draftState, action.payload)
      )
    case constants.UPDATE_DEFAULT_RULE_GROUP_ACTION:
      return produce(state, (draftState) =>
        updateDraftDefaultRuleGroupAction(draftState, action.payload)
      )
    case constants.POINT_DEFAULT_GROUP_ACTION_TO_NEW_GROUP:
      return produce(state, (draftState) =>
        pointDefaultGroupActionToNewGroup(draftState, action.payload)
      )
    case constants.DRAFT_SAVE_DONE:
      return produce(state, (draftState) =>
        recordDraftSaved(draftState, action.payload)
      )
    case constants.START_DRAFT_SAVE:
      return produce(state, (draftState) => startDraftSaving(draftState))
    case constants.DRAFT_SAVE_FAILED:
      return produce(state, (draftState) =>
        failedSavingDraft(draftState, action.payload)
      )
    case constants.REVERT_TO_PUBLISHED_VERSION:
      return produce(state, (draftState) =>
        revertToPublishedVersion(draftState)
      )
    case constants.SET_SHOW_PUBLISHED:
      return produce(state, (draftState) =>
        setShowPublished(draftState, action.payload)
      )
    case constants.UPDATE_DRAFT_DEFAULT_USER_IDS:
      return produce(state, (draftState) =>
        updateDraftDefaultUserIds(draftState, action.payload)
      )
    case constants.SHOW_SELECT_CONTINUE_TO_GROUP:
      return produce(state, (draftState) =>
        showSelectContinueToGroup(draftState, action.payload)
      )
    case constants.CHOOSE_SELECT_CONTINUE_TO_GROUP:
      return produce(state, (draftState) =>
        chooseSelectContinueToGroup(draftState, action.payload)
      )
    case constants.CANCEL_SELECT_CONTINUE_TO_GROUP:
      return produce(state, (draftState) =>
        cancelSelectContinueToGroup(draftState, action.payload)
      )
    case constants.DELETE_RULE_GROUP:
      return produce(state, (draftState) =>
        deleteRuleGroup(draftState, action.payload)
      )
    case constants.MOVE_RULE:
      return produce(state, (draftState) =>
        moveRule(draftState, action.payload)
      )
    case constants.SET_SHOW_ADDING_NEW_RULE:
      return produce(state, (draftState) =>
        setShowAddingNewRule(draftState, action.payload)
      )
    case constants.SET_SHOW_EDIT_ACTION:
      return produce(state, (draftState) =>
        setShowEditAction(draftState, action.payload)
      )
    case constants.SET_HIDE_OPT_IN_FLOW:
      return produce(state, (draftState) =>
        setHideFallbackOptInFlow(draftState, action.payload)
      )
    default:
      return newState
  }
}

export default reducer
